import { useTranslation } from 'react-i18next';
import { DateRange } from '../../../models/timeline-calendar.types';
import { TimelineNavigation } from '../../components/timeline-navigation';
import { TIMELINE_SCALE_LABELS, type TimelineScaleLabel } from '../../../libs/timeline-config';
import type { TimelineConfig } from '../../../models/timeline-calendar.types';
import type { ReactNode } from 'react';

interface TimelineControlsProps {
  controlsRef: React.RefObject<HTMLDivElement>;
  headerContent?: ReactNode;
  dateRange: DateRange;
  currentConfig: TimelineConfig;
  setScale: (label: TimelineScaleLabel) => void;
  movePeriod: (direction: 'prev' | 'next') => void;
  resetToToday: () => void;
}

const segmentButtonStyle = (active: boolean) => ({
  backgroundColor: active ? 'var(--color-accent)' : 'transparent',
  color: active ? 'var(--color-accent-fg)' : 'var(--color-text-secondary)',
  fontWeight: active ? 500 : 400,
} as const);

export const TimelineControls = ({
  controlsRef,
  headerContent,
  dateRange,
  currentConfig,
  setScale,
  movePeriod,
  resetToToday,
}: TimelineControlsProps) => {
  const { t } = useTranslation();
  return (
    <div
      ref={controlsRef}
      className="sticky top-0 z-30 flex items-center gap-2 px-3"
      data-testid="timeline-controls"
      style={{
        height: 'var(--toolbar-height)',
        backgroundColor: 'var(--color-bg-surface)',
        borderBottom: '1px solid var(--color-border)',
      }}
    >
      {/* Header content (title + back button) */}
      {headerContent && (
        <div className="min-w-0 flex-1">{headerContent}</div>
      )}

      {/* Separator */}
      <div className="h-5 w-px shrink-0" style={{ backgroundColor: 'var(--color-border)' }} />

      {/* Scale selector (hidden on mobile) */}
      <div
        className="hidden shrink-0 items-center gap-0.5 rounded-md p-0.5 sm:flex"
        style={{
          backgroundColor: 'var(--color-bg-app)',
          border: '1px solid var(--color-border)',
        }}
      >
        {TIMELINE_SCALE_LABELS.map((label) => (
          <button
            key={label}
            type="button"
            data-testid={`timeline-scale-${label}`}
            className="rounded-sm px-2.5 py-1 text-[11px] transition-colors"
            style={segmentButtonStyle(currentConfig.label === label)}
            onMouseEnter={(e) => {
              if (currentConfig.label !== label) {
                e.currentTarget.style.backgroundColor = 'var(--color-bg-hover)';
              }
            }}
            onMouseLeave={(e) => {
              if (currentConfig.label !== label) {
                e.currentTarget.style.backgroundColor = 'transparent';
              }
            }}
            onClick={() => setScale(label)}
          >
            {t(`scale.${label}`)}
          </button>
        ))}
      </div>

      {/* Navigation (hidden on mobile) */}
      <div className="hidden sm:block">
        <TimelineNavigation
          dateRange={dateRange}
          onPrevious={() => movePeriod('prev')}
          onNext={() => movePeriod('next')}
          onToday={resetToToday}
        />
      </div>
    </div>
  );
};
